import React, { useEffect, useState } from 'react';
import useResponsive from '../utils/useResponsive';
import { formatearMoneda } from '../utils/formatMoney';
import FiltroUsuarios from './FiltroUsuarios';
import { useFiltroUsuarios } from '../filtroUsuarios';
import { getUsuarioActual } from '../apiAuth';

const API = '/api';

const money = (v) => `$${formatearMoneda(Math.round(Number(v) || 0))}`;

const dateDisplay = (v) => {
  if (!v) return '—';
  const [y, m, d] = String(v).slice(0, 10).split('-');
  return `${d}-${m}-${y}`;
};

// Config por tipo: título, etiqueta del monto, etiqueta de la fecha, y si
// aplica la columna "Notaría Asignada" (solo En Avalúo / En Infonavit, donde
// no puede haber más de un banco por cliente).
const CONFIG = {
  evaluacion: { titulo: 'En Evaluación', accent: '#1a3a6e', montoLabel: 'Monto a Evaluar', fechaLabel: 'Fecha de Ingreso a Evaluación', notaria: false },
  autorizadas: { titulo: 'Autorizadas', accent: '#16a34a', montoLabel: 'Monto Autorizado', fechaLabel: 'Fecha de Autorización', notaria: false, vigencia: true },
  avaluo: { titulo: 'En Avalúo', accent: '#f97316', montoLabel: 'Monto Autorizado', fechaLabel: 'Fecha de Ingreso a Avalúo', notaria: true },
  infonavit: { titulo: 'En Infonavit', accent: '#1e40af', montoLabel: 'Monto Autorizado', fechaLabel: 'Fecha de Ingreso a Infonavit', notaria: true },
  'firmadas-mes': { titulo: 'Firmadas — Del Mes', accent: '#4338ca', montoLabel: 'Monto Firmado', fechaLabel: 'Fecha de Firma', notaria: false },
  'firmadas-anio': { titulo: 'Firmadas — Del Año', accent: '#16a34a', montoLabel: 'Monto Firmado', fechaLabel: 'Fecha de Firma', notaria: false },
};

const SeguimientoDetalleActivas = ({ tipo, onVolver }) => {
  const { isMobile } = useResponsive();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');

  const cfg = CONFIG[tipo];
  const esAdminPlus = getUsuarioActual()?.rol === 'administrador_plus';
  const filtroUsuariosKey = useFiltroUsuarios().join(',');

  // Se recarga también al cambiar el filtro de usuarios del Administrador Plus.
  useEffect(() => {
    setDatos(null); setError('');
    fetch(`${API}/seguimiento-clientes/dashboard/${tipo}`)
      .then(r => r.json())
      .then(d => (d.error ? setError(d.error) : setDatos(d)))
      .catch(() => setError('No se pudo cargar el detalle'));
  }, [tipo, filtroUsuariosKey]);

  const st = {
    container: { padding: isMobile ? 12 : 24, maxWidth: 1300, margin: '0 auto' },
    stickyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20, position: 'sticky', top: 'var(--app-header-h, 120px)', backgroundColor: '#fff', zIndex: 20, padding: '10px 4px', borderBottom: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' },
    titulo: { fontSize: 24, fontWeight: 'bold', margin: 0, color: cfg?.accent || '#1a3a6e' },
    volverBtn: { padding: '10px 20px', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 14, backgroundColor: '#d1d5db', color: '#374151' },
    tableWrap: { border: '1px solid #e5e7eb', borderRadius: 6, overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: 700 },
    th: { backgroundColor: '#1a3a6e', color: 'white', padding: '10px 8px', textAlign: 'left', fontSize: 12.5 },
    td: { padding: '8px 8px', borderBottom: '1px solid #e5e7eb', fontSize: 13.5 },
    tdNum: { padding: '8px 8px', borderBottom: '1px solid #e5e7eb', fontSize: 13.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    totalBar: { display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'baseline', padding: '14px 8px', fontSize: 15, fontWeight: 'bold', color: cfg?.accent || '#1a3a6e' },
    vacio: { padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 14 },
  };

  if (!cfg) return null;

  return (
    <div style={st.container}>
      <div style={st.stickyHeader}>
        <h2 style={st.titulo}>🤝 Hipotecas Activas — {cfg.titulo}</h2>
        <FiltroUsuarios etiqueta={false} ancho={260} contenedorStyle={{ marginLeft: 'auto' }} />
        <button style={st.volverBtn} onClick={onVolver}>← Volver</button>
      </div>

      {error && <p style={{ color: '#dc2626' }}>{error}</p>}
      {!error && !datos && <p>Cargando...</p>}

      {datos && (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Nombre</th>
                <th style={st.th}>Banco</th>
                <th style={{ ...st.th, textAlign: 'right' }}>{cfg.montoLabel}</th>
                <th style={st.th}>Tipo de Crédito</th>
                <th style={st.th}>{cfg.fechaLabel}</th>
                {cfg.vigencia && <th style={st.th}>Fecha de Vigencia</th>}
                {cfg.notaria && <th style={st.th}>Notaría Asignada</th>}
              </tr>
            </thead>
            <tbody>
              {datos.filas.map((f, i) => (
                <tr key={`${f.prospecto_id}-${f.banco}`} style={{ backgroundColor: i % 2 ? 'white' : '#f9fafb' }}>
                  <td style={st.td}>
                    {(f.nombre_cliente || '—').toUpperCase()}
                    {esAdminPlus && <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>👤 {f.usuario_nombre || '-'}</div>}
                  </td>
                  <td style={st.td}>{f.banco}</td>
                  <td style={st.tdNum}>{money(f.monto)}</td>
                  <td style={st.td}>{f.tipo_credito || '—'}</td>
                  <td style={st.td}>{dateDisplay(f.fecha)}</td>
                  {cfg.vigencia && <td style={st.td}>{dateDisplay(f.fecha_vigencia)}</td>}
                  {cfg.notaria && <td style={st.td}>{f.notaria_asignada || '—'}</td>}
                </tr>
              ))}
              {datos.filas.length === 0 && (
                <tr><td style={st.vacio} colSpan={5 + (cfg.notaria ? 1 : 0) + (cfg.vigencia ? 1 : 0)}>Sin registros en este estatus.</td></tr>
              )}
            </tbody>
          </table>
          {datos.filas.length > 0 && (
            <div style={st.totalBar}>
              <span>Total:</span>
              <span>{money(datos.total)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeguimientoDetalleActivas;
